import React, { useState, Fragment, useEffect } from 'react';
import classNames from 'classnames';
import { Checkbox, Form, Button, Popover } from 'Components/nui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormWrapper } from 'Components/form';
import {
  DateRangeField,
  DateField,
  SelectField,
  DecimalField,
  RadioSelectField,
} from 'Components/form/fields';
import {
  hasDateRange,
  MAX_SAFE_INTEGER,
  mergeSchema,
  normalizeChoices,
  strBoolIsTruthy,
  getChoicesWithConstraint,
} from 'Components/form/utils';
import { RadioButtonWidget } from 'Components/form/widgets';
import { LoadingState, ModalContext } from 'Components/Layout';
import { ClassicResult, ShowResult } from 'Components/Result';
import { useStoreState } from 'easy-peasy';
import { promisify } from 'es6-promisify';
import dayjs from 'dayjs';
import { useAsync } from 'react-use';
import { Product } from '~/models';
import FNumber from 'Components/FNumber';
import { parseSearch, getDecimalCount, capitalize } from '~/utils';
import { PresetButton } from './PresetButton';
import { PresetDropdown } from './PresetDropdown';
import { ScheduleButton } from './ScheduleButton';
import { api } from '~/api';
import Helmet from 'Components/Helmet';
import { useParams, Link } from 'react-router-dom';
import DisplayedPrices from 'Pages/Products/DisplayedPrices';
import FilesField from 'Components/form/FilesField';
import Variants from 'Pages/Tenders/Edit/Variants';
import { prepareInitialDataFromSchema } from '~/utils';
import { Radio } from 'antd';
import { Alert } from 'Components/nui';
import { inArrayIf } from '~/utils';
import { FormTotals } from './FormTotals';
import { getFreightIds, getAddrInfo, getAddrChoice } from '~/utils';
import { useLockBodyScroll } from '~/hooks';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { ProductTagsFilter } from './ProductTagsFilter';
import { Wizard, WizardStep } from 'Components/Wizard';
import FormLayout from './FormLayout';
import {
  ORDER_STATUS,
  PENDING_STATUS_ID,
} from '~/store/models/orders/constants';
import { Button as AntButton } from 'antd';
import { useTradableProducts, useSolutionTags } from '~/hooks/useProducts';
import { useInvalidateMarketOrders } from '~/pages/Market/hooks/useMarketOrders';
import { useFormCloseProtection } from '~/hooks/useFormCloseProtection';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { tagMarketplaceTradingActivity } from '~/sentryMarketplace';

/**
 * TODO
 * - Describe workflow
 * - Product itself needs validation and error processing as well
 * - Prefetched and then staled product / product.loading can cause issues.
 */

/**
 * Steps:
 * Take getFormWrapper as example
 * 1. Define the schema list.
 * 2. Optional. Merge the schema with the schema from server-side.
 * 3. Feed the schema list to get formWrapper
 *
 * Check OrderForm
 * 4. Render the formWrapper to render the components (and bind them to underlying form).
 * 5. When submitting and client-side validation passed, collect the data from formWrapper
 */

const addrMatch = (addr, freightId) => {
  const [, , addrFreightInfo] = addr ?? [];

  return (
    !addr ||
    freightId === addrFreightInfo?.zone ||
    freightId === addrFreightInfo?.port
  );
};

const inOptions = (addrOptions, freightId) =>
  !addrOptions || addrOptions.includes(freightId);

const isOptionalRoute = (toaddrOptions, fromaddrOptions) => (route) =>
  inOptions(toaddrOptions, route.destination.id) &&
  inOptions(fromaddrOptions, route.source.id);

const isMatchingRoute = (toaddr, fromaddr) => (route) =>
  addrMatch(toaddr, route.destination.id) &&
  addrMatch(fromaddr, route.source.id);

const getOrderPlacedTagData = (result, orderType) => {
  const orderId = result?.order?.id;
  const orderIds = Array.isArray(result?.orders)
    ? result.orders.map((order) => order.id).filter(Boolean)
    : [];

  return {
    ...(orderId ? { order_id: orderId } : {}),
    ...(!orderId && orderIds.length ? { order_ids: orderIds } : {}),
    order_type: orderType,
  };
};

const EtaLabel = () => (
  <FormattedMessage id="orderform-field-eta-label" defaultMessage="ETA(s)" />
);

export function useFormWrapper({
  form,
  product,
  etd,
  initialData,
  schema,
  solution,
  orderType,
  hideTotals = false,
}) {
  const { formatMessage } = useIntl();
  const division = form.getFieldValue('division');
  const exportIntent = strBoolIsTruthy(form, schema, 'exportable');
  const exportdocsRequired = strBoolIsTruthy(form, schema, 'exportdocs');
  const heatTreatedPalletsRequired = strBoolIsTruthy(
    form,
    schema,
    'heattreatedpallets'
  );

  const fromaddr = form.getFieldValue('fromaddr');

  const toaddr = form.getFieldValue('toaddr');

  const totaldeliveries = form.getFieldValue('totaldeliveries') ?? 1;

  const volume = form.getFieldValue('volume') ?? 0;

  const totalVolume = totaldeliveries * volume;

  const hasFlatFee = solution?.exportprice?.flatfee;

  const [files, setFiles] = useState([]);
  const [expiryDays, setExpiryDays] = useState(0);
  const [prodModalVis, setProdModalVis] = useState(false);
  const tradeActions = useStoreState((state) => state.auth.userTradeActions);

  const solutionColumns = useStoreState((state) => state.auth.solutionColumns);
  const anonymousOrders = useStoreState(
    (state) => state.auth.solutionColumns?.anonymous?.value
  );

  const [realBuyerID, setRealBuyerID] = useState();
  const [realSellerID, setRealSellerID] = useState();

  const [realBuyerUserChoices, setRealBuyerUserChoices] = useState([]);
  const [realSellerUserChoices, setRealSellerUserChoices] = useState([]);

  const [etdMode, setEtdMode] = useState(
    initialData.deliveryfrequency ? 'periodic' : 'etd'
  );

  const counterpartyMode = form.getFieldValue('counterparty_mode');

  useEffect(() => {
    if (!realBuyerID) {
      setRealBuyerUserChoices([]);
    } else {
      (async () => {
        try {
          const data = await api.getData2(`/client-users/${realBuyerID}`);
          setRealBuyerUserChoices(data?.choices || []);
        } catch (error) {
          setRealBuyerUserChoices([]);
        }
      })();
    }
  }, [realBuyerID, setRealBuyerUserChoices]);

  useEffect(() => {
    if (!realSellerID) {
      setRealSellerUserChoices([]);
    } else {
      (async () => {
        try {
          const data = await api.getData2(`/client-users/${realSellerID}`);
          setRealSellerUserChoices(data?.choices || []);
        } catch (error) {
          setRealSellerUserChoices([]);
        }
      })();
    }
  }, [realSellerID, setRealSellerUserChoices]);

  const docsChoices = [
    {
      key: 'yes',
      label: (
        <FormattedMessage
          id="orderform-field-docs-yes-label"
          defaultMessage="Yes"
        />
      ),
      value: 'true',
    },
    {
      key: 'no',
      label:
        orderType === 'offer' ? (
          <FormattedMessage
            id="orderform-field-docs-no-label"
            defaultMessage="No"
          />
        ) : (
          <FormattedMessage
            id="orderform-field-docs-notrequired-label"
            defaultMessage="Not required"
          />
        ),
      value: 'false',
    },
  ];

  useEffect(() => {
    if (Object.keys(initialData).length === 0) {
      form.setFieldsValue({ indexedprice: false });
      setFiles([]);
    }
  }, [initialData?.id]);

  // Some fields returned from API include choices as object, inside which the key is product id.
  function getChoicesByProduct({ choices }) {
    return normalizeChoices(
      Array.isArray(choices) ? choices : choices?.[product.id] || []
    );
  }

  const getChoicesByDivision =
    ({ choices }) =>
    () => {
      const divisionId = form.getFieldValue('division');
      return normalizeChoices(
        Array.isArray(choices) ? choices : choices?.[divisionId] || []
      );
    };

  const otherCurrencies = Object.keys(schema)
    .filter((key) => key.startsWith('price_'))
    .map((item) => item.split('_')[1]);

  const [curVis, setCurVis] = useState(() =>
    Object.fromEntries(
      otherCurrencies.map((item) => [item, !!initialData?.[`price_${item}`]])
    )
  );

  const priceDecimalCount = getDecimalCount(product.step);
  const priceStep = product.step;

  const indexedprice =
    form.getFieldsValue(['indexedprice']).indexedprice === 'true';
  const openprice = form.getFieldsValue(['openprice']).openprice === 'true';

  const loadingId = form.getFieldValue('loading');
  const splittableLoadings = schema?.loading?.splittable || [];
  // form can get updated in render cycle, thus need to run this dynamically in field spec or render()
  const getLoading = () =>
    (product?.loading || []).find((item) => item.id === loadingId);

  const counterpartyChoices = [
    {
      key: 'all',
      label: (
        <FormattedMessage id="orderform-field-all-label" defaultMessage="All" />
      ),
      value: normalizeChoices(schema?.counterparty?.choices),
    },
  ];

  const now = dayjs();

  // etd and quarters
  const hasPeriodic = 'deliveryfrequency' in schema;
  const hasQuarters = 'quarters' in schema;
  const etdLabel = hasQuarters ? (
    <FormattedMessage
      id="orderform-field-etdquarters-label"
      defaultMessage="Estimated time of departure..."
    />
  ) : (
    <FormattedMessage
      id="orderform-field-etd-label"
      defaultMessage="Estimated time of departure"
    />
  );
  const hasEtd = 'frometd' in schema;
  const etdField = new DateRangeField({
    name: 'etd',
    label: etdLabel,
    // When etd field is there and not filled, this field is required, otherwise follow schema.
    required: () => {
      const isQuarters = hasQuarters
        ? !form.getFieldValue('quarters')
        : !!schema?.frometd?.required;
      return isQuarters && etdMode === 'etd';
    },
    fromDate: now.clone(),
    tz: solution?.timezone,
    props: {
      onChange: (v) => {
        // XXX v here is not normalized and maybe empty (which is still OK though)
        if (hasQuarters && v) {
          form.setFieldsValue({ quarters: undefined });
        }
        setEtdMode('etd');
      },
    },
    initialValue: etd ?? initialData.etd,
  });

  const startdateField = new DateField({
    name: 'frometd',
    label: (
      <FormattedMessage
        id="orderform-section-delivery-details-field-start-date-label"
        defaultMessage="Start date"
      />
    ),
    required: () => etdMode === 'periodic',
    tz: solution?.timezone,
    fromDate: now.clone(),
    props: {
      onChange(v) {
        form.setFieldsValue({
          frometd: v,
          toetd: v,
        });
        setEtdMode('periodic');
      },
    },
    initialValue: initialData?.frometd,
  });

  const deliveryfrequencyField = new SelectField({
    name: 'deliveryfrequency',
    label: schema?.deliveryfrequency?.label,
    required: () => etdMode === 'periodic',
    props: () => {
      return {
        onChange(v) {
          form.setFieldsValue({
            deliveryfrequency: v,
          });
          setEtdMode('periodic');
        },
      };
    },
    choices: normalizeChoices(schema?.deliveryfrequency?.choices || [], false),
    initialValue: initialData?.deliveryfrequency,
  });

  const totaldeliveriesField = new DecimalField({
    name: 'totaldeliveries',
    label: schema?.totaldeliveries?.label,
    format: '%d',
    min: 2,
    required: () => etdMode === 'periodic',
    initialValue: initialData?.totaldeliveries,
  });

  const quartersField = new SelectField({
    name: 'quarters',
    widget: RadioButtonWidget,
    choices: normalizeChoices(schema?.quarters?.choices || []).filter(
      (x) => x.value !== 'range'
    ),
    label: hasEtd ? (
      <FormattedMessage
        id="orderform-field-quarters-label"
        defaultMessage="Or split over quarterly period"
      />
    ) : (
      etdLabel
    ),
    required: () =>
      hasEtd
        ? !hasDateRange(form.getFieldValue('etd'))
        : !!schema?.quarters?.required,
    props: {
      onChange: (v) => {
        if (hasEtd) {
          form.setFieldsValue({ etd: undefined });
        }
      },
    },
    initialValue: initialData?.quarters,
  });

  const privatebidTypeInitial =
    initialData.destinations == null ||
    (Array.isArray(initialData.destinations)
      ? initialData.destinations.length === 0
      : Object.keys(initialData.destinations).length === 0)
      ? 'counterparty'
      : 'destinations';
  const deorcoField = new RadioSelectField({
    name: 'deorco',
    type: 'Boolean',
    choices: [
      {
        key: 'counterparty',
        label: (
          <FormattedMessage
            id="orderform-privatebid-type-counterparty-label"
            defaultMessage="By counterparty"
          />
        ),
        value: 'counterparty',
      },
      {
        key: 'destinations',
        label: (
          <FormattedMessage
            id="orderform-privatebid-type-destinations-label"
            defaultMessage="By destinations"
          />
        ),
        value: 'destinations',
      },
    ],
    label: (
      <FormattedMessage
        id="orderform-field-private-bid-type"
        defaultMessage="Private {orderType, select, offer {offer} bid {bid} other {{orderType}}} type"
        values={{ orderType }}
      />
    ),
    required: true,
    initialValue: privatebidTypeInitial,
  });

  // Production Date
  const hasProductionDate = 'productiondate' in schema;
  const productionDateField = new DateField({
    name: 'productiondate',
    label: schema?.productiondate?.label,
    required: () => form.getFieldValue('age') !== 'fresh',
    tz: solution?.timezone,
    initialValue: initialData?.productiondate,
  });

  const realBuyerUserField = new SelectField({
    name: 'realbuyeruser',
    label: schema?.realbuyeruser?.label,
    choices: () => normalizeChoices(realBuyerUserChoices),
  });

  const realSellerUserField = new SelectField({
    name: 'realselleruser',
    label: schema?.realselleruser?.label,
    choices: () => normalizeChoices(realSellerUserChoices),
  });

  // Not great but easier
  // This might not be an ideal abstraction, but putting logic in render is even worse.
  let fields = [
    {
      name: 'division',
      choices: normalizeChoices,
    },
    {
      name: 'realparty',
      label:
        orderType === 'offer'
          ? formatMessage({
              id: 'orderform-field-seller-label',
              defaultMessage: 'Seller',
            })
          : formatMessage({
              id: 'orderform-field-buyer-label',
              defaultMessage: 'Buyer',
            }),
      choices: () => normalizeChoices(schema?.realparty?.choices),
      initialValue: schema?.realparty?.value,
      props: () => ({ disabled: schema?.realparty?.disabled }),
    },
    {
      name: 'realpartyuser',
      label:
        orderType === 'offer'
          ? formatMessage({
              id: 'orderform-field-sellers-user-label',
              defaultMessage: "Seller's user",
            })
          : formatMessage({
              id: 'orderform-field-buyers-user-label',
              defaultMessage: "Buyer's user",
            }),
      choices: () => normalizeChoices(schema?.realpartyuser?.choices),
      initialValue: schema?.realpartyuser?.value,
    },
  ];

  if (orderType === 'bid') {
    if (schema?.realbuyer?.choices) {
      fields.push({
        name: 'realbuyer',
        choices: normalizeChoices,
        props: {
          onChange: (x) => {
            setRealBuyerID(x);
          },
        },
        initialValue: schema?.realparty?.value,
      });
    }
    if (realBuyerID) {
      fields.push({
        name: 'realbuyeruserfield',
        render: () => <>{realBuyerUserField.render({ form })}</>,
        serializeField: (values) => ({
          realbuyeruser: realBuyerUserField.serialize(values.realbuyeruser),
        }),
      });
    }
    if (form.getFieldValue('realbuyer')) {
      fields.push({
        name: 'realseller',
        choices: normalizeChoices,
        props: {
          onChange: (x) => {
            setRealSellerID(x);
          },
        },
      });
      if (realSellerID) {
        fields.push({
          name: 'realselleruserfield',
          render: () => <>{realSellerUserField.render({ form })}</>,
          serializeField: (values) => ({
            realselleruser: realSellerUserField.serialize(
              values.realselleruser
            ),
          }),
        });
      }
    }
  }

  if (orderType === 'offer') {
    if (schema?.realseller?.choices) {
      fields.push({
        name: 'realseller',
        choices: normalizeChoices,
        props: {
          onChange: (x) => {
            setRealSellerID(x);
          },
        },
      });
    }
    if (realSellerID) {
      fields.push({
        name: 'realselleruserfield',
        render: () => <>{realSellerUserField.render({ form })}</>,
        serializeField: (values) => ({
          realselleruser: realSellerUserField.serialize(values.realselleruser),
        }),
      });
    }
    if (form.getFieldValue('realseller')) {
      fields.push({
        name: 'realbuyer',
        choices: normalizeChoices,
        props: {
          onChange: (x) => {
            setRealBuyerID(x);
          },
        },
      });
      if (realBuyerID) {
        fields.push({
          name: 'realbuyeruserfield',
          render: () => <>{realBuyerUserField.render({ form })}</>,
          serializeField: (values) => ({
            realbuyeruser: realBuyerUserField.serialize(values.realbuyeruser),
          }),
        });
      }
    }
  }

  if (schema.attributes) {
    const sep = schema.attributes.separator;
    Object.entries(schema.attributes.fields)
      .sort(([a], [b]) => a.localeCompare(b))
      // dynamic
      .forEach(([name, spec]) => {
        fields.push({
          ...spec,
          name: ['attributes', name].join(sep),
          choices: spec.choices
            .map(([value, label]) => ({
              key: value,
              value,
              label,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
          psuedo: true,
          props: {
            placeholder: (
              <FormattedMessage
                id="orderform-field-attributes-placeholder"
                defaultMessage="Select {attribute}"
                values={{ attribute: name }}
              />
            ),
          },
        });
      });
  }

  fields.push(
    {
      name: 'currency',
      choices: normalizeChoices,
    },
    {
      name: 'etd',
      render: () => {
        if (!hasEtd && !hasQuarters) return null;
        const etdNodes = () =>
          etdMode === 'etd' && (
            <Fragment key="form-item-etd-fragment">
              {hasEtd && etdField.render({ form })}
              {hasQuarters && quartersField.render({ form })}
            </Fragment>
          );
        return !hasPeriodic ? (
          etdNodes()
        ) : (
          <div
            key="form-item-etd-fragment"
            className={classNames(
              'ant-row ant-form-item etd-price-volume',
              `etd-mode-${etdMode}`
            )}
          >
            <Radio.Group
              className="form-etd-group"
              value={etdMode}
              onChange={(e) => {
                const { value } = e.target;
                if (!['etd', 'periodic'].includes(value)) return;
                if (etdMode === value) return;
                setEtdMode(value);
              }}
            >
              <Radio
                className="form-etd-etd-radio"
                name="delivery-type"
                value="etd"
              >
                <div className="form-etd-etd-radio-label">{etdLabel}</div>
              </Radio>
              {etdMode === 'etd' && (
                <div className="ml-28 mb-10">{etdNodes()}</div>
              )}

              {hasPeriodic && (
                <>
                  <Radio
                    className="form-etd-periodic-radio mr-0"
                    name="delivery-type"
                    value="periodic"
                  >
                    <div className="form-etd-periodic-radio-label">
                      <FormattedMessage
                        id="orderform-section-delivery-details-field-striporder-label"
                        defaultMessage="Strip order"
                      />

                      <Popover
                        overlay={
                          <span>
                            <FormattedMessage
                              id="orderform-section-delivery-details-field-striporder-helptext"
                              defaultMessage="A strip order is the buying or selling of contracts in sequential delivery periods traded as a single transaction."
                            />
                          </span>
                        }
                      >
                        <span className="popover-link">?</span>
                      </Popover>
                    </div>
                  </Radio>
                  {etdMode === 'periodic' && (
                    <div className="ml-28">
                      <div className="form-etd-periodic-start mb-10">
                        {startdateField.render({ form })}
                      </div>
                      <div className="form-etd-periodic-period">
                        {deliveryfrequencyField.render({ form })}
                        {totaldeliveriesField.render({ form })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Radio.Group>
          </div>
        );
      },
      serializeField: (values) => {
        if (!hasEtd && !hasQuarters) return null;
        if (etdMode === 'etd') {
          const etd = etdField.serialize(values.etd);
          const quarters = quartersField.serialize(values.quarters);
          return {
            frometd: etd?.[0],
            toetd: etd?.[1],
            quarters: etd ? 'range' : quarters,
          };
        } else if (etdMode === 'periodic') {
          const deliveryfrequency = deliveryfrequencyField.serialize(
            values.deliveryfrequency
          );
          const totaldeliveries = deliveryfrequencyField.serialize(
            values.totaldeliveries
          );
          const startdate = startdateField.serialize(values.frometd);
          return {
            frometd: startdate,
            deliveryfrequency,
            totaldeliveries,
          };
        }
      },
    },
    {
      render: () => <hr key="random-hr-logging-warnings-and-pissing-me-of" />,
    },
    {
      name: 'loading',
      choices: (product?.loading || [])
        .map((x) => ({
          key: x.id,
          value: x.id,
          label: x.desc,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      props: {
        onChange: (v) => {
          if (!splittableLoadings.includes(v)) {
            form.setFieldsValue({ splittable: false });
          }
        },
      },
    },
    // {
    //   name: 'indexedpriceheading',
    //   render: () => (
    //     <p key="stop-logging-warnings" className="title p-0">
    //       <FormattedMessage
    //         id="orderform-section-indexed-price-heading"
    //         defaultMessage="Price and volume"
    //       />
    //     </p>
    //   ),
    // },
    {
      name: 'indexedprice',
      required: true,
      initialValue: initialData.indexedprice ?? schema.indexedprice?.value,
      initialData: initialData.indexedprice ?? schema.indexedprice?.value,
      props: {
        disabled: schema.indexedprice?.disabled,
      },
    },
    ...(indexedprice && product.index
      ? [
          ...(product.index.type === 'Multiplier'
            ? [
                {
                  name: 'price',
                  label_suffix: schema?.price?.label_index,
                  step: product.index.step,
                  initialValue: 1,
                  min: product.index.step,
                  max: MAX_SAFE_INTEGER,
                  required: true,
                  props: {
                    disabled: openprice,
                  },
                },
                {
                  name: 'startprice',
                  label_suffix: schema?.startprice?.label_index,
                  step: product.index.step,
                  initialValue: 1,
                  min: product.index.step,
                  max: MAX_SAFE_INTEGER,
                  required: true,
                  props: {
                    disabled: openprice,
                  },
                },
              ]
            : [
                {
                  name: 'price',
                  label_suffix: schema?.price?.label_index,
                  step: product.index.step,
                  min: -9999999,
                  required: true,
                  props: {
                    disabled: openprice,
                  },
                },
                {
                  name: 'startprice',
                  label_suffix: schema?.startprice?.label_index,
                  step: product.index.step,
                  min: -9999999,
                  required: true,
                  props: {
                    disabled: openprice,
                  },
                },
              ]),
          {
            name: 'reserve_price',
            step: product.step,
          },
          {
            name: 'indexdate',
            fromDate: now.clone(),
            initialValue: now.clone().add(1, 'weeks').day(1),
            props: {
              disabled: schema.indexdate?.disabled,
            },
          },
          {
            name: 'fixed_index',
            props: {
              disabled: schema.fixed_index?.disabled,
            },
          },
          {
            render: () => <hr key="no-more-warnings-2" className="mt-5" />,
          },
        ]
      : [
          {
            name: 'price',
            step: product.step,
            min: 'openprice' in schema ? 0 : product.step,
            props: {
              disabled: openprice,
            },
          },
          {
            name: 'startprice',
            initialValue: 0,
            step: product.step,
            props: {
              disabled: openprice,
            },
          },
          {
            name: 'reserve_price',
            step: product.step,
          },
        ]),
    {
      name: 'volume',
      label: (
        <>
          {etdMode === 'etd' ? (
            schema?.volume?.label
          ) : (
            <FormattedMessage
              id="orderform-field-volume-per-delivery-label"
              defaultMessage="Per delivery volume"
            />
          )}
        </>
      ),
      step: () => getLoading()?.qty || 1,
      props: () => ({ disabled: !getLoading() || schema.volume?.disabled }),
    },
    {
      name: 'viewCustomerPrices',
      render: () => {
        if (hideTotals) return null;

        const { price, startprice } = form.getFieldsValue([
          'price',
          'startprice',
        ]);
        const effectivePrice = price ?? startprice;

        return (
          <>
            {!indexedprice &&
              solution.freight &&
              tradeActions.includes('sell') && (
                <Form.Item key="form-item-value">
                  <div className="p-0 totals">
                    <div>
                      <Dialog
                        open={prodModalVis}
                        onOpenChange={setProdModalVis}
                      >
                        <DialogContent
                          className="tw:sm:max-w-[650px] tw:z-[9999]"
                          overlayClassName="tw:z-[9999]"
                        >
                          <DialogHeader>
                            <DialogTitle>
                              <FormattedMessage
                                id="customer-prices"
                                defaultMessage="Customer prices"
                              />
                            </DialogTitle>
                          </DialogHeader>
                          <DisplayedPrices
                            auto={
                              !!(
                                effectivePrice &&
                                (loadingId || product?.loading?.[0]?.id)
                              )
                            }
                            productId={product.id}
                            init={{
                              price: effectivePrice,
                              loading: loadingId || product?.loading?.[0]?.id,
                              fromaddr: form.getFieldValue('fromaddr'),
                            }}
                          />
                        </DialogContent>
                      </Dialog>
                      <Button
                        htmlType="button"
                        type="link"
                        className="nui-button customer-prices nui-reverse m-0 p-0"
                        onClick={() => setProdModalVis(true)}
                      >
                        <span>
                          <FormattedMessage
                            id="orderform-field-viewcustomerprices-label"
                            defaultMessage="View customer prices"
                          />
                        </span>
                      </Button>
                    </div>
                  </div>
                </Form.Item>
              )}
          </>
        );
      },
    },
    {
      name: 'counteronly',
      label: (
        <strong className="all-black">{capitalize(orderType)} type</strong>
      ),
      formItemProps: {
        className: 'form-item-vertical',
      },
      initialData: !!schema.counteronly?.value,
      initialValue: !!schema.counteronly?.value,
      choices: [
        {
          label: (
            <FormattedMessage
              id="orderform-field-counteronly-firm"
              defaultMessage="Directly tradeable (Firm)"
            />
          ),
          value: 'false',
          key: 'no',
        },
        {
          label: (
            <FormattedMessage
              id="orderform-field-counteronly-sfc"
              defaultMessage="Subject to final confirmation (SFC)"
            />
          ),
          value: 'true',
          key: 'yes',
        },
      ],
    },
    ...(schema.variants
      ? [
          new Variants({
            form,
            schema: {
              ...schema,
              startprice: { ...schema.startprice, step: product.step },
            },
            order: initialData,
            orderType,
          }),
        ]
      : []),
    {
      name: 'summary',
      render: () => {
        const fields = form.getFieldsValue([
          'price',
          'startprice',
          'indexedprice',
          'indexdate',
          'volume',
          'totaldeliveries',
          'deliveryfrequency',
          'frometd',
          'loading',
          'counterparty',
          'counterparty_mode',
          'openprice',
          'variants',
        ]);

        const counterpartyChoices = normalizeChoices(
          schema?.counterparty?.choices || []
        );

        const isTender = schema.startprice != null;

        return (
          <FormTotals
            key="form-totals"
            product={product}
            fields={{
              ...fields,
              counterpartyMode: fields.counterparty_mode,
              counterpartyChoices: counterpartyChoices,
            }}
            isTender={isTender}
          />
        );
      },
    },
    {
      render: () => <hr key="no-more-warnings-1" />,
    }
  );

  // Other currencies

  if (otherCurrencies && otherCurrencies.length > 0) {
    fields.push({
      name: 'otherCurrencies',
      render: () => (
        <div className="currencies" key="currencies">
          <h3>
            <FormattedMessage
              id="orderform-field-currencies-additional-heading"
              defaultMessage="Additional {count, plural, one {currency} other {currencies}}"
              values={{ count: otherCurrencies.length }}
            />
          </h3>
          <p className="smaller justify m-0">
            <FormattedMessage
              id="orderform-field-currencies-helptext-additionalcurrencies"
              defaultMessage="The currency conversion rate is based on a daily average currency conversion feed."
            />
          </p>
        </div>
      ),
    });

    otherCurrencies.forEach((cur) => {
      fields.push({
        name: 'currencyOption',
        render: () => (
          <div className="currency-option" key={`${cur}-vis`}>
            <Checkbox
              className="mb-0"
              checked={curVis[cur]}
              onChange={() =>
                setCurVis({ ...curVis, ...{ [cur]: !curVis[cur] } })
              }
            >
              <FormattedMessage
                id="orderform-field-currencies-helptext-pricein"
                defaultMessage="Price in"
              />{' '}
              {cur}
            </Checkbox>
          </div>
        ),
      });

      if (curVis[cur]) {
        const newPriceField = new DecimalField({
          name: `price_${cur}`,
          step: product.step,
          min: 'openprice' in schema ? 0 : product.step,
          initialValue: initialData?.[`price_${cur}`],
          required: true,
        });

        fields.push({
          name: 'estimateCurCurrency',
          render: () => {
            const curPrice = form.getFieldValue('price') || 0;
            const value = curPrice * schema[`price_${cur}`].exchange;
            return (
              <div className="currency-field" key={cur}>
                {newPriceField.render({ form })}
                <div className="currency-exchange">
                  <span>
                    <FormattedMessage
                      id="orderform-field-currencies-helptext-estimated"
                      defaultMessage="Estimated in this currency"
                    />{' '}
                  </span>
                  <strong>
                    {cur}{' '}
                    <FNumber
                      value={Math.round(value / priceStep) * priceStep}
                      decimalCount={priceDecimalCount}
                    />
                  </strong>
                </div>
              </div>
            );
          },
          serializeField: (values) => {
            if (!(`price_${cur}` in schema)) return null;
            return {
              [`price_${cur}`]: newPriceField.serialize(values[`price_${cur}`]),
            };
          },
        });
      }
    });
    // }
    fields.push({
      render: () => <hr key="cur-bottom-line" className="mt-5 mb-20" />,
    });
  }

  fields = fields.concat([
    /**
     * For quarters & frometd/toetd, we could either:
     * - render them in same render(), or
     * - put them in standalone fields, or
     * - invent some combined field type for them
     * - TODO ListField, ObjectField and accepting array of names, in future maybe =p
     */
    {
      name: 'callofftime',
      required: () => {
        const _etd = form.getFieldValue('etd');
        if (!_etd) return false;
        return _etd[0].clone().add(28, 'days') <= _etd[1];
      },
    },
    {
      name: 'origins',
      choices: normalizeChoices,
      props: {
        placeholder: (
          <FormattedMessage
            id="orderform-field-origin-placeholder"
            defaultMessage="Select Origin"
          />
        ),
      },
    },
    {
      name: 'incoterms',
      choices: (fieldSchema) =>
        getChoicesWithConstraint(fieldSchema, form, () =>
          getChoicesByDivision(fieldSchema)()
        ),
    },
    {
      name: 'locations',
      choices: normalizeChoices,
      props: {
        placeholder: (
          <FormattedMessage
            id="orderform-field-locations-placeholder"
            defaultMessage="Select Locations"
          />
        ),
      },
    },
    {
      name: 'regions',
      choices: normalizeChoices,
      props: {
        placeholder: (
          <FormattedMessage
            id="orderform-field-region-placeholder"
            defaultMessage="Select Region"
          />
        ),
      },
    },
    {
      name: 'fromaddr',
      choices: getChoicesByDivision,
    },
    {
      name: 'warehouse',
      choices: getChoicesByDivision,
    },
  ]);

  const privatebid = form.getFieldValue('privatebid') === 'true';
  if (privatebid) {
    if ('destinations' in schema) {
      fields.push({
        name: 'destinationsLabel',
        render: () => <>{deorcoField.render({ form })}</>,
      });
    }

    const formValue = form.getFieldValue('deorco');
    const deorcoValue =
      formValue !== undefined
        ? formValue
        : initialData?.deorco || privatebidTypeInitial;

    if (deorcoValue === 'destinations') {
      fields.push({
        name: 'destinations',
        choices: getChoicesByProduct,
        required: true,
      });
    } else {
      fields.push({
        name: 'counterparty',
        choices: counterpartyChoices,
        required: privatebid,
        props: {
          treeSelect: true,
          disabled: counterpartyMode === 'public',
          onChange: (value) => {
            form.setFieldsValue({
              counterparty: value,
            });
          },
        },
      });
      if (form.getFieldValue('counterparty')?.length > 1) {
        fields.push({
          name: 'counterparty_mode',
          choices: normalizeChoices(schema?.counterparty_mode?.choices || []),
          required: true,
        });
      }
    }
  }

  fields = fields.concat([
    {
      name: 'toaddr',
      choices: getChoicesByDivision,
    },
    {
      name: 'etatext',
      render: () => {
        const etaFreightType = ['port', 'zone'];
        const etd = form.getFieldValue('etd');

        if (
          orderType === 'bid' &&
          toaddr &&
          etaFreightType.includes(schema?.toaddr?.freight) &&
          etd &&
          solution.shippingroutes &&
          schema?.toaddr?.choices
        ) {
          const toaddrChoice = getAddrChoice(division)(schema.toaddr.choices);
          const fromaddrChoice =
            schema?.fromaddr?.choices &&
            getAddrChoice(division)(schema.fromaddr.choices);
          const toaddrInfo = getAddrInfo(toaddr)(toaddrChoice);
          const fromaddrInfo =
            fromaddrChoice && getAddrInfo(fromaddr)(fromaddrChoice);

          const toaddrFreightIds = getFreightIds(toaddrChoice);
          const fromaddrFreightIds =
            fromaddrChoice && getFreightIds(fromaddrChoice);

          const optionalRoutes = solution.shippingroutes.filter(
            isOptionalRoute(toaddrFreightIds, fromaddrFreightIds)
          );

          const matchingRoutes = optionalRoutes.filter(
            isMatchingRoute(toaddrInfo, fromaddrInfo)
          );
          if (!matchingRoutes.length) {
            return (
              <Alert
                className="mb-10"
                type="info"
                key={`from-${fromaddr}-to-${toaddr}`}
              >
                <EtaLabel />
                <span className="block ">
                  <FormattedMessage
                    id="orderform-eta-no-shipping-routes"
                    defaultMessage="No shipping routes found"
                  />
                </span>
              </Alert>
            );
          }
          const sortedRoutes = matchingRoutes.sort(
            (route1, route2) => route1.duration - route2.duration
          );
          return (
            <Alert
              className="mb-10"
              type="info"
              key={`from-${fromaddr}-to-${toaddr}`}
            >
              <EtaLabel />

              {sortedRoutes.map((route) => (
                <div key={route.id} className="mb-10">
                  <div>
                    <strong className="etd">
                      {etd[0] && (
                        <>
                          <FormattedDate
                            key="eta-from"
                            value={dayjs(etd[0])
                              .add(route.duration, 'day')
                              .toDate()}
                            day="numeric"
                            month="long"
                            year="numeric"
                          />{' '}
                          -{' '}
                        </>
                      )}
                      <FormattedDate
                        key="eta-to"
                        value={dayjs(etd[1])
                          .add(route.duration, 'day')
                          .toDate()}
                        day="numeric"
                        month="long"
                        year="numeric"
                      />
                    </strong>
                  </div>
                  <small>
                    <FormattedMessage
                      id="orderform-field-shippingroute"
                      defaultMessage="From {source} to {destination}"
                      values={{
                        source: route.source.name,
                        destination: route.destination.name,
                      }}
                    />
                  </small>
                </div>
              ))}
            </Alert>
          );
        }

        return null;
      },
    },
    {
      name: 'freightavailable',
      choices: docsChoices,
    },

    {
      name: 'gfsi',
      choices: docsChoices,
    },
    {
      name: 'export',
      choices: docsChoices,
    },
    {
      name: 'halal',
      choices: docsChoices,
    },
    {
      name: 'kosher',
      choices: docsChoices,
    },
    {
      name: 'expiry',
      fromDate: now.clone(),
      initialValue: now.clone().add(7, 'day').startOf('hour'),
      minuteStep: 15,
      props: {
        showToday: false,
        onChange: (v) => {
          setExpiryDays(v ? dayjs(v).diff(now.clone(), 'days') : 0);
        },
      },
      label: (
        <>
          {schema?.expiry?.label}
          {expiryDays > 7 && (
            <span className="icon-attention-circled expiry-warning">
              <FormattedMessage
                id="orderform-field-validuntil-helptext"
                defaultMessage="Expires in {expiryDays} days"
                values={{ expiryDays: expiryDays }}
              />
            </span>
          )}
        </>
      ),
    },
    {
      name: 'brands',
      choices: getChoicesByProduct,
    },

    {
      name: 'productionmonth',
      choices: normalizeChoices(schema.productionmonth?.choices)?.sort((a, b) =>
        a.key.localeCompare(b.key)
      ),
    },

    {
      name: 'exportable',
      // NOTE: need BE label
      initialValue: schema?.exportable?.value,
      choices: hasFlatFee ? undefined : docsChoices,
      required: true,
    },

    ...inArrayIf(
      !('exportable' in schema) || (exportIntent && orderType === 'bid'),
      {
        name: 'exportdocs',
        initialValue: schema?.exportdocs?.value,
        choices: docsChoices,
        required: true,
      }
    ),

    {
      name: 'exporttext',
      render: () =>
        exportIntent &&
        hasFlatFee &&
        exportdocsRequired &&
        orderType === 'bid' ? (
          <Alert
            hasicon
            type="info"
            className="alert-fee"
            key="alert-exportfee"
          >
            <FormattedMessage
              id="orderform-export-fee-notice"
              defaultMessage="Please note a {price} {currency} flat fee will be added to this order. {moreInfoLink}"
              values={{
                price: (
                  <strong style={{ width: 'auto' }}>
                    {solution?.exportprice?.price}
                  </strong>
                ),
                currency: solution.currency,
                moreInfoLink: solution.exportprice.file ? (
                  <a
                    href={solution.exportprice.file.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage
                      id="orderform-export-fee-more-info-link"
                      defaultMessage="View more information here"
                    />
                  </a>
                ) : null,
              }}
            />
          </Alert>
        ) : (
          <React.Fragment key="exportintent-value-empty" />
        ),
    },

    ...inArrayIf(exportIntent && orderType === 'bid', {
      name: 'heattreatedpallets',
      choices: docsChoices,
      required: true,
      initialValue: schema?.heattreatedpallets?.value,
    }),

    {
      name: 'heattreatedpalletstext',
      render: () =>
        exportIntent &&
        heatTreatedPalletsRequired &&
        totalVolume > 0 &&
        solution?.exportprice?.heattreatedfee && (
          <Alert
            hasicon
            type="info"
            className="pallets-request"
            key="pallets-request"
          >
            <h3>
              <FormattedMessage
                id="orderform-heat-treated-pallets-summary-heading"
                defaultMessage="Heat-treated pallets summary"
              />
            </h3>
            <div>
              <blockquote>
                <FNumber
                  value={parseFloat(solution.exportprice.heattreatedfee)}
                  decimalCount={getDecimalCount(
                    parseFloat(solution.exportprice.heattreatedfee)
                  )}
                  unit={solution.currency}
                />{' '}
                &times;{' '}
                <FNumber
                  value={totalVolume}
                  decimalCount={getDecimalCount(totalVolume)}
                />{' '}
                ={' '}
                <FNumber
                  value={
                    parseFloat(solution.exportprice.heattreatedfee) *
                    totalVolume
                  }
                  decimalCount={2}
                  unit={solution.currency}
                />
              </blockquote>
            </div>
          </Alert>
        ),
    },

    ...inArrayIf(exportIntent, {
      name: 'exportcountry',
      choices: normalizeChoices(schema?.exportcountry?.choices),
      initialValue: schema?.exportcountry?.value,
    }),
    {
      name: 'splittable',
      hidden: !splittableLoadings.includes(loadingId),
    },
    {
      name: 'privatebid',
    },
    {
      name: 'age',
      choices: getChoicesByProduct,
      // Empty choices might be provided
      // TODO Hide the field directly if choices are empty
      required: ({ choices, required }) => {
        if (getChoicesByProduct({ choices }).length === 0) return false;
        return required;
      },
    },
    {
      name: 'productiondate',
      render: () => {
        if (
          !form.getFieldValue('age') ||
          form.getFieldValue('age') === 'fresh' ||
          !hasProductionDate
        )
          return null;
        return (
          <div className="pl-0 pr-0">
            {productionDateField.render({ form })}
          </div>
        );
      },
      serializeField: (values) => {
        if (!hasProductionDate) return null;
        return {
          productiondate: productionDateField.serialize(values.productiondate),
        };
      },
    },
    {
      name: 'buyer',
      choices: normalizeChoices,
    },
    {
      name: 'consignee',
      choices: normalizeChoices,
    },
    {
      name: 'note',
    },
    {
      name: 'comment',
    },
    {
      name: 'reference',
    },
    {
      name: 'openprice',
      serializeField: (values) => {
        if (!('openprice' in schema)) return null;
        const { price } = form.getFieldsValue();
        return { openprice: String(price === 0) };
      },
      required: true,
      initialValue:
        (schema?.openprice?.value ?? schema?.price?.value === 0)
          ? 'true'
          : 'false',
      props: {
        onChange: (e) => {
          if (e.target.value === 'true') {
            form.setFieldsValue({ price: 0, startprice: 0 });
          }
        },
      },
    },
    {
      name: 'files',
      render: () => {
        if (!schema?.files) return null;
        return (
          <FilesField
            key="form-files"
            initialData={initialData}
            accept={(schema?.files?.accepted || []).join(', ')}
            files={files}
            setFiles={setFiles}
          />
        );
      },
      serializeField: () => {
        if (!schema?.files) return null;
        return files.length ? { files } : undefined;
      },
    },
    {
      name: 'attachments',
      choices: normalizeChoices,
      initialValue: schema?.attachments?.value || [],
      initialData: schema?.attachments?.value || [],
    },
    {
      name: 'anonymous',
      choices: [
        {
          label: (
            <FormattedMessage
              id="orderform-field-docs-yes-label"
              defaultMessage="Yes"
            />
          ),
          value: 'true',
          key: 'yes',
        },
        {
          label: (
            <FormattedMessage
              id="orderform-field-docs-no-label"
              defaultMessage="No"
            />
          ),
          value: 'false',
          key: 'no',
        },
      ],
      initialValue: anonymousOrders,
      type: 'Boolean',
      required: true,
    },
    {
      name: 'scheduledtime',
      hidden: true,
    },
  ]);

  const merge = mergeSchema(schema, fields);
  return new FormWrapper(form, initialData, merge);
}

function OrderForm({
  form,
  product,
  etd,
  initialData,
  schema,
  submitState,
  orderType,
  handleModalClose,
  prevLocation,
  solution,
  tags,
  allHasTags,
  productGroupChoices,
  setProductGroupChoices,
  productField,
}) {
  const invalidateMarketOrders = useInvalidateMarketOrders();
  const [schedulePopupVisible, setSchedulePopupVisible] = useState(false);
  const [scheduleError, setScheduleError] = useState([]);

  const scheduledTime = form.getFieldValue('scheduledtime');
  const setScheduledTime = (time) => {
    form.setFieldsValue({ scheduledtime: time });
    // Clear error when time is set
    setScheduleError([]);
  };

  // Track initial values for dirty detection
  const initialValuesRef = React.useRef(null);
  const currentProduct = form.getFieldValue('product');
  const currentVolume = form.getFieldValue('volume');
  const currentPrice = form.getFieldValue('price');
  const currentLoading = form.getFieldValue('loading');

  React.useEffect(() => {
    if (!initialValuesRef.current && product) {
      initialValuesRef.current = {
        product: currentProduct,
        volume: currentVolume,
        price: currentPrice,
        loading: currentLoading,
      };
    }
  }, [product, currentProduct, currentVolume, currentPrice, currentLoading]);

  const isDirty = React.useMemo(() => {
    if (!initialValuesRef.current) return false;
    const initial = initialValuesRef.current;
    return (
      currentProduct !== initial.product ||
      currentVolume !== initial.volume ||
      currentPrice !== initial.price ||
      currentLoading !== initial.loading
    );
  }, [currentProduct, currentVolume, currentPrice, currentLoading]);

  const {
    showConfirm,
    handleCloseAttempt,
    handleConfirmClose,
    handleCancelClose,
  } = useFormCloseProtection(isDirty, handleModalClose);

  // Register close handler with drawer for X button protection
  const { registerCloseHandler } = React.useContext(ModalContext);
  React.useEffect(() => {
    registerCloseHandler(handleCloseAttempt);
    return () => registerCloseHandler(null);
  }, [registerCloseHandler, handleCloseAttempt]);

  // TODO Move this wrapper to outer if it affects performance
  const formWrapper = useFormWrapper({
    form,
    product,
    etd,
    initialData,
    schema,
    solution,
    orderType,
  });
  // Already bound
  const validateFieldsAndScroll = promisify(form.validateFieldsAndScroll);

  async function handleSubmit(e) {
    e?.preventDefault();
    // Client-side validation
    try {
      await validateFieldsAndScroll();
    } catch (err) {
      return;
    }
    // Server-side validation
    submitState.setLoading(true);
    const serialized = {
      ...formWrapper.serialize(),
      product: product.id,
    };

    const { files, ...fields } = serialized;
    const data = new FormData();
    for (const [name, value] of Object.entries(fields)) {
      if (value !== undefined) {
        if (Array.isArray(value)) for (const v of value) data.append(name, v);
        else data.append(name, value);
      }
    }
    if (!data.has('splittable')) {
      data.append('splittable', false);
    }
    data.append('sell', orderType === 'offer');
    if (files) {
      for (const file of files) data.append('files[]', file);
    }

    const button = e?.nativeEvent.submitter;
    if (button?.name === 'save-pending') {
      data.append('status', PENDING_STATUS_ID);
      data.delete('scheduledtime');
    }

    try {
      const result = await api.getData(
        { type: 'orders', getResult: (x) => x },
        null,
        { method: 'post', data }
      );
      // TODO postProcess
      if (button?.name !== 'save-pending') {
        tagMarketplaceTradingActivity(
          'order_placed',
          getOrderPlacedTagData(result, orderType)
        );
      }
      submitState.setResult(result);

      switch (prevLocation.pathname) {
        case '/':
        case '/overview':
          invalidateMarketOrders();
          break;

        default:
          break;
      }
    } catch (err) {
      const errors = err?.response?.data?.errors?.[0]?.description;
      if (errors) {
        // TODO scrollIntoView
        formWrapper.setErrors(errors);
        // If there's a scheduledtime error, open the schedule popup and set error
        if (errors.scheduledtime) {
          setScheduleError(errors.scheduledtime);
          setSchedulePopupVisible(true);
        }
      } else {
        // report the unexcepted error
        submitState.setResult(err);
      }
    }
    submitState.setLoading(false);
  }

  return (
    <div>
      <Form onSubmit={handleSubmit}>
        <FormLayout
          form={form}
          formWrapper={formWrapper}
          product={product}
          orderType={orderType}
          productSelector={
            <ProductSelector
              tags={tags}
              allHasTags={allHasTags}
              productGroupChoices={productGroupChoices}
              setProductGroupChoices={setProductGroupChoices}
              productField={productField}
            />
          }
        />
        <WizardStep>
          <Form.Item>
            {formWrapper.renderField('summary')}
            <div className="button-set place-order-button-set">
              {form.getFieldValue('realbuyer') &&
              form.getFieldValue('realseller') ? (
                <Button
                  htmlType="submit"
                  className={`nui-${orderType}`}
                  disabled={submitState.loading}
                >
                  <FormattedMessage
                    id="orderform-button-fok-label"
                    defaultMessage="Create trade"
                  />
                </Button>
              ) : (
                <AntButton.Group className={`nui-button nui-${orderType}`}>
                  <Button
                    htmlType="submit"
                    disabled={submitState.loading || schedulePopupVisible}
                  >
                    {scheduledTime ? (
                      <FormattedMessage
                        id="orderform-button-scheduleorder-label"
                        defaultMessage="Schedule {type, select, offer {offer} bid {bid} other {{type}}}"
                        values={{ type: orderType }}
                      />
                    ) : (
                      <FormattedMessage
                        id="orderform-button-placeorder-label"
                        defaultMessage="Place {type, select, offer {offer} bid {bid} other {{type}}}"
                        values={{ type: orderType }}
                      />
                    )}
                  </Button>
                  {Object.hasOwn(schema, 'scheduledtime') && (
                    <span>
                      <ScheduleButton
                        visible={schedulePopupVisible}
                        setVisible={setSchedulePopupVisible}
                        onVisibleChange={setSchedulePopupVisible}
                        disabled={submitState.loading || schedulePopupVisible}
                        scheduledTime={scheduledTime}
                        setScheduledTime={setScheduledTime}
                        error={scheduleError}
                      />
                    </span>
                  )}
                </AntButton.Group>
              )}

              <Button
                name="save-pending"
                htmlType="submit"
                type="reverse"
                disabled={submitState.loading || schedulePopupVisible}
              >
                <FormattedMessage
                  id="orderform-button-save-pending-label"
                  defaultMessage="Save as pending"
                />
              </Button>

              <PresetButton
                product={product}
                orderType={orderType}
                formWrapper={formWrapper}
              />

              <Button
                htmlType="button"
                type="simple"
                onClick={handleCloseAttempt}
                className="ml-10 pl-0 pr-0"
                style={{ minWidth: '0' }}
              >
                <FormattedMessage
                  id="orderform-button-cancel-label"
                  defaultMessage="Cancel"
                />
              </Button>
            </div>
          </Form.Item>
        </WizardStep>
      </Form>
      <ConfirmDialog
        open={showConfirm}
        onOpenChange={handleCancelClose}
        title={
          <FormattedMessage
            id="orderform-close-confirm-title"
            defaultMessage="Discard changes?"
          />
        }
        confirmLabel={
          <FormattedMessage
            id="orderform-close-confirm-discard"
            defaultMessage="Discard"
          />
        }
        cancelLabel={
          <FormattedMessage
            id="orderform-close-confirm-keep"
            defaultMessage="Keep editing"
          />
        }
        onConfirm={handleConfirmClose}
      >
        <FormattedMessage
          id="orderform-close-confirm-message"
          defaultMessage="You have unsaved changes. Are you sure you want to close?"
        />
      </ConfirmDialog>
    </div>
  );
}

const WrappedOrderForm = Form.create()(OrderForm);

function submitSuccess(result, submitState) {
  const orderType = result?.order?.type || '';
  const isMultipleOrders = result?.orders && Array.isArray(result.orders);
  const firstOrder = isMultipleOrders ? result.orders[0] : result?.order;
  const isPending =
    firstOrder?.status === ORDER_STATUS.PENDING ||
    firstOrder?.status === ORDER_STATUS.SCHEDULED;
  const orderCount = isMultipleOrders ? result.orders.length : 1;

  return (
    <ClassicResult
      title={
        isPending ? (
          <FormattedMessage
            id="orderform-submit-successful-save-saved-heading"
            defaultMessage="{orderCount, plural, one {Order saved as pending} other {Orders saved as pending}}"
            values={{ orderCount }}
          />
        ) : (
          <FormattedMessage
            id="orderform-submit-successful-heading"
            defaultMessage="{orderCount, plural, one {Order placed} other {Orders placed}}"
            values={{ orderCount }}
          />
        )
      }
    >
      <p>{result?.message}</p>
      <hr className="mb-15 mt-15" />
      <div className="button-set">
        <Link
          to={
            isMultipleOrders
              ? isPending
                ? '/orders'
                : '/'
              : isPending
                ? `/orders/${result?.order?.pid}`
                : `/?order=${result?.order?.pid || ''}`
          }
        >
          <Button type={orderType} className="small mb-0">
            <FormattedMessage
              id="orderform-button-view-label-new"
              defaultMessage="View {orderType, select, offer {offer} bid {bid} other {{orderType}}}"
              values={{ orderType: orderType }}
            />
          </Button>
        </Link>

        <Button
          type="reverse"
          className="small mb-0"
          onClick={() => {
            submitState.setResult();
          }}
        >
          <FormattedMessage
            id="orderform-button-view-addanother"
            defaultMessage="Add another"
          />
        </Button>
      </div>
    </ClassicResult>
  );
}

export const submitError = (result, submitState, handleModalClose) => (
  <ClassicResult
    success={false}
    title={
      <FormattedMessage
        id="orderform-error-heading"
        defaultMessage="Something went wrong"
      />
    }
  >
    <p>
      <FormattedMessage
        id="orderform-error-submit"
        defaultMessage="Failed to submit. Please try again later."
      />
    </p>
    <hr className="mb-15 mt-15" />
    <div className="button-set">
      <Button
        type="primary"
        className="nui-button small mb-0"
        onClick={() => {
          // Clear result page
          submitState.setResult();
        }}
      >
        <FormattedMessage
          id="orderform-button-tryeditagain-label"
          defaultMessage="Try edit again"
        />
      </Button>
      <Button type="simple" onClick={handleModalClose} className="small mb-0">
        <FormattedMessage
          id="orderform-button-closepanel-label"
          defaultMessage="Close panel"
        />
      </Button>
    </div>
  </ClassicResult>
);

// Entry component for selecting tradable product to decide form schema.
export function OrderNew({ location, handleModalClose, prevLocation }) {
  useLockBodyScroll();
  const { formatMessage } = useIntl();
  const solution = useStoreState((state) => state.auth.solution);
  const tags = useSolutionTags(solution?.id);
  const tagIdArray = tags.map((tag) => tag.id);
  const [productGroupChoices, setProductGroupChoices] = useState([
    'ungrouped',
    ...tagIdArray,
  ]);

  const { orderType } = useParams();

  const title = formatMessage(
    {
      id: 'orderform-page-title',
      defaultMessage: 'Place market {orderType}',
    },
    { orderType }
  );

  // Here set global timezone to the one of the selected solution.
  // TODO Refer Google's solution which
  //  - shows current timezone clearly
  //  - notify user when local timezone is different from the master configured timezone
  //  - show timezone picker in event form
  const tradableProducts = useTradableProducts(orderType, solution?.id);
  const productById = (id) => {
    const product = tradableProducts.find((p) => p.id === id);
    return product && new Product(product);
  };

  // Parse location.search to prefill the fields as initial value.
  // TODO could also use data from location state
  // const initialData = parseSearch(location.search);
  const [initialData, setInitialData] = useState(parseSearch(location.search));
  const [order, setOrder] = useState(initialData?.order);
  // XXX this is so dumb; I'm only doing it to be consistent :(
  const [tenderOrder] = useState(initialData?.['tender-order']);
  const [preset, setPreset] = useState();

  // Get product from initial value
  const [product, setProduct] = useState(productById(initialData?.product));

  const etd = initialData.etd;

  const allHasTags = tradableProducts.every((item) => item?.tags?.length > 0);

  // tags load asynchronously
  useEffect(() => {
    if (!allHasTags) {
      setProductGroupChoices(['ungrouped', ...tags.map((tag) => tag.id)]);
    } else {
      setProductGroupChoices(tags.map((tag) => tag.id));
    }
  }, [tags, setProductGroupChoices]);

  const productId = product?.id;

  const changeProduct = (value) => {
    setProduct(productById(value));
    setOrder(undefined);
    setPreset(undefined);
  };

  // Load schema of the division of the selected product
  const schemaState = useAsync(() => {
    // When no product selected, returns { loading: false, value: undefined }
    if (!productId) return Promise.resolve();
    let params = { sell: orderType === 'offer' };
    let id = 'neworder';
    if (preset) {
      params.preset = preset;
    } else if (order) {
      id = `${order}/schema`;
    } else if (tenderOrder) {
      params['tender-offer-id'] = tenderOrder;
      delete params.sell;
    } else {
      params.product = productId;
    }
    return api.getData({ type: 'orders', id: id }, params);
  }, [productId, order, preset]);

  const productMatchesGroup = (product) =>
    product.tags.length > 0
      ? product.tags.some((tag) => productGroupChoices.includes(tag.id))
      : productGroupChoices.includes('ungrouped');

  const productFilteredByGroup =
    tags.length === 0
      ? tradableProducts
      : tradableProducts.filter(productMatchesGroup);

  const productChoices = (() => {
    const marketplaceProducts = productFilteredByGroup.filter(
      (p) => p.marketplace === true
    );
    const groupedProducts = marketplaceProducts.reduce((acc, p) => {
      const ownerName = p?.owner?.name;
      const groupKey = ownerName
        ? formatMessage(
            {
              id: 'orderform-products-of-owner',
              defaultMessage: 'Products of {owner}',
            },
            { owner: ownerName }
          )
        : formatMessage({
            id: 'orderform-products',
            defaultMessage: 'Products',
          });

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(p);
      return acc;
    }, {});

    return Object.entries(groupedProducts).map(([group, products]) => ({
      key: group,
      label: group,
      value: products.map((x) => ({ key: x.id, value: x.id, label: x.name })),
    }));
  })();

  // TODO product field is not put in a form, thus it is possible to submit invalid product,
  //  if the product has been changed after form is shown.
  const productField = new SelectField({
    name: 'product',
    required: true,
    props: {
      disabled: schemaState && schemaState.loading,
      // no choice but hardcode - we dont have schema yet
      placeholder: (
        <FormattedMessage
          id="orderform-field-products-placeholder"
          defaultMessage="Select a product"
        />
      ),
      onChange: changeProduct,
      value: productId,
    },
    choices: productChoices,
  });

  return (
    <div
      role="region"
      aria-label={formatMessage({
        id: 'orderform-aria-label',
        defaultMessage: 'New Order',
      })}
      className="wrapper"
    >
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <h2>
        <FormattedMessage
          id="orderform-label-place-order-type"
          defaultMessage="Place market {orderType, select, offer {offer} bid {bid} other {{orderType}}}"
          values={{ orderType: orderType }}
        />
      </h2>

      <ShowResult
        renderSuccess={submitSuccess}
        renderError={submitError}
        handleModalClose={handleModalClose}
      >
        {(submitState) => (
          <div className="nui-form order-form new-order-form">
            <PresetDropdown
              orderType={orderType}
              setPreset={setPreset}
              setProduct={setProduct}
            />
            <Wizard>
              {!product && (
                <WizardStep
                  stepId={1}
                  title={formatMessage({
                    id: 'orderform-wizard-step-product',
                    defaultMessage: 'Product',
                  })}
                >
                  <ProductSelector
                    tags={tags}
                    allHasTags={allHasTags}
                    productGroupChoices={productGroupChoices}
                    setProductGroupChoices={setProductGroupChoices}
                    productField={productField}
                  />
                </WizardStep>
              )}

              <LoadingState state={product ? schemaState : null}>
                {(schema) => (
                  <WrappedOrderForm
                    // Reset form data when product changes
                    key={`form-${productId}`}
                    orderType={orderType}
                    schema={schema}
                    product={product}
                    etd={etd}
                    initialData={prepareInitialDataFromSchema(schema)}
                    submitState={submitState}
                    handleModalClose={handleModalClose}
                    prevLocation={prevLocation}
                    solution={solution}
                    tags={tags}
                    allHasTags={allHasTags}
                    productGroupChoices={productGroupChoices}
                    setProductGroupChoices={setProductGroupChoices}
                    productField={productField}
                  />
                )}
              </LoadingState>
            </Wizard>
          </div>
        )}
      </ShowResult>
    </div>
  );
}

const ProductSelector = ({
  tags,
  allHasTags,
  productGroupChoices,
  setProductGroupChoices,
  productField,
}) => {
  const { formatMessage } = useIntl();
  return (
    <>
      {tags.length > 0 && (
        <div className="ant-row ant-form-item">
          <ProductTagsFilter
            productGroupChoices={productGroupChoices}
            setProductGroupChoices={setProductGroupChoices}
            tags={tags}
          />
        </div>
      )}
      <Form.Item required={true}>
        <div className="ant-col ant-form-item-label">
          <label
            className="ant-form-item-required"
            title={formatMessage({
              id: 'orderform-field-product-title',
              defaultMessage: 'Product',
            })}
          >
            <FormattedMessage
              id="orderform-field-product-label"
              defaultMessage="Product"
            />
          </label>
        </div>
        {productField.renderUnbound()}
      </Form.Item>
    </>
  );
};
